import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFileExcel, FaFilePdf, FaFileInvoice, FaEdit, FaTrash, FaStar, FaMapMarkerAlt, FaPhone, FaEnvelope, FaSpinner, FaMoneyBillWave, FaHandHoldingUsd, FaCoins, FaReceipt, FaChevronDown } from 'react-icons/fa';
import { Modal, Button, Form, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Joyride from 'react-joyride';
import { toast } from 'react-toastify';
import { handleApiResponse, showApiMessage } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import './Bajeti.css';
import { useAuthContext } from '../../context/AuthContext';
import { budgetApi } from '../../api/budget';
import { spApi } from '../../api/serviceProviders';
import { useDebounce } from '../../hooks/useDebounce';
import apiClient from '../../api/index';

// Separate Modal Imports
import OngezaBajetiModal from '../../modals/bajetiModal/OngezaBajetiModal';
import HaririBajetiModal from '../../modals/bajetiModal/HaririBajetiModal';
import FutaBajetiModal from '../../modals/bajetiModal/FutaBajetiModal';
import ProvidersBajetiModal from '../../modals/bajetiModal/ProvidersBajetiModal';

const Bajeti = () => {
  const { user, events, isAuthenticated } = useAuthContext();
  const currentEvent = events?.[0];

  // State
  const [budgetItems, setBudgetItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showProvidersModal, setShowProvidersModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    service_category_id: '',
    amount: '',
    amount_paid: '',
    new_payment: '',
    description: ''
  });
  const [runTour, setRunTour] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm]);

  // Fetch data
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchBudgetItems();
      fetchCategories();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchBudgetItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await budgetApi.getBudgets();

      // The API returns { success: true, data: [ ... array of items ... ] }
      let items = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      } else if (Array.isArray(response?.data)) {
        items = response.data;
      } else if (Array.isArray(response)) {
        items = response;
      }

      const formatted = items.map(item => ({
        ...item,
        // The API provides the category name, robustly extract it
        category_name: item.service_category?.name || item.service_category?.category || item.category_name || item.item || item.name || 'Unknown',
        amount_formatted: `TZS ${(parseFloat(item.amount) || 0).toLocaleString()}`,
        amount_paid_formatted: `TZS ${(parseFloat(item.amount_paid) || 0).toLocaleString()}`,
        remaining: Math.max((parseFloat(item.amount) || 0) - (parseFloat(item.amount_paid) || 0), 0),
        remaining_formatted: `TZS ${Math.max((parseFloat(item.amount) || 0) - (parseFloat(item.amount_paid) || 0), 0).toLocaleString()}`
      }));
      setBudgetItems(formatted);
    } catch (err) {
      console.error('Error fetching budget:', err);
      // If it's a 404, it just means no budget items exist yet, which is fine
      if (err.response && err.response.status !== 404) {
        setError('Failed to load budget items');
        toast.error('Failed to load budget');
      }
      setBudgetItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await budgetApi.getServiceCategories();
      const apiPayload = response?.data || response;
      const dataWrapper = apiPayload?.data ?? apiPayload;
      const categoriesArray = dataWrapper?.data ?? dataWrapper;
      const normalizedCats = Array.isArray(categoriesArray) ? categoriesArray : [];
      const finalCats = normalizedCats.map(cat => ({
        ...cat,
        name: cat.category || cat.name || 'Unknown'
      }));
      setCategories(finalCats);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
      toast.error('Failed to load categories');
    }
  };

  const fetchProvidersByCategory = async (categoryId) => {
    setProvidersLoading(true);
    setProviders([]);
    try {
      const response = await spApi.getProvidersByCategory(categoryId);
      let providersList = response?.data || response || [];
      if (!Array.isArray(providersList)) providersList = Object.values(providersList);
      setProviders(providersList);
    } catch (err) {
      console.error('Error fetching providers:', err);
      toast.error('Failed to load service providers');
    } finally {
      setProvidersLoading(false);
    }
  };

  const handleShowProviders = async (categoryId, categoryName) => {
    setSelectedCategory({ id: categoryId, name: categoryName });
    await fetchProvidersByCategory(categoryId);
    setShowProvidersModal(true);
  };

  const handleAddEdit = () => {
    setEditingItem(null);
    setFormData({ service_category_id: '', amount: '', amount_paid: '', new_payment: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      service_category_id: item.service_category_id,
      amount: item.amount,
      amount_paid: item.amount_paid,
      new_payment: '',
      description: item.description || ''
    });
    setShowModal(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setSubmitting(true);
    try {
      const response = await budgetApi.deleteBudget(itemToDelete.id);
      handleApiResponse(response);
      setShowDeleteModal(false);
      setItemToDelete(null);
      fetchBudgetItems();
    } catch (err) {
      console.error('Delete error:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.service_category_id || !formData.amount) {
      toast.warning('Please select a category and enter amount');
      return;
    }
    setSubmitting(true);
    try {
      const currentPaid = parseFloat(formData.amount_paid) || 0;
      const newPayment = parseFloat(formData.new_payment) || 0;

      const payload = {
        service_category_id: parseInt(formData.service_category_id),
        amount: parseFloat(formData.amount),
        amount_paid: editingItem ? (currentPaid + newPayment) : (parseFloat(formData.amount_paid) || 0),
        description: formData.description || null
      };
      if (editingItem) {
        const response = await budgetApi.updateBudget(editingItem.id, payload);
        handleApiResponse(response);
      } else {
        const response = await budgetApi.createBudget(payload);
        handleApiResponse(response);
      }
      setShowModal(false);
      fetchBudgetItems();
    } catch (err) {
      console.error('Save error:', err);
      handleApiResponse(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Columns & Data Fetchers matching QuickReportModal.js for ExportUtility
  const budgetCols = [
    { header: 'Huduma/Bidhaa', dataKey: 'item_name' },
    { header: 'Kundi', dataKey: 'category' },
    { header: 'Kadirio (TZS)', dataKey: 'estimated_cost' },
    { header: 'Halisi (TZS)', dataKey: 'actual_cost' },
    { header: 'Kilicholipwa (TZS)', dataKey: 'paid_amount' },
    { header: 'Deni (TZS)', dataKey: 'balance' }
  ];

  const expCols = [
    { header: 'Matumizi', dataKey: 'item_name' },
    { header: 'Kundi', dataKey: 'category' },
    { header: 'Kiasi (TZS)', dataKey: 'amount' },
    { header: 'Mlipwaji', dataKey: 'paid_to' },
    { header: 'Tarehe', dataKey: 'date' }
  ];

  const fetchBudgetData = async () => {
    try {
      const response = await apiClient.get('/export/budget/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.budget || dataPayload?.items || dataPayload?.data || dataPayload || [];
      const list = Array.isArray(rawData) && rawData.length > 0 ? rawData : budgetItems;
      return list.map(item => {
        const est = parseFloat(item.estimated_cost || item.budget_amount || item.amount || 0);
        const act = parseFloat(item.actual_cost || item.actual_amount || item.cost || 0);
        const paid = parseFloat(item.paid_amount || item.amount_paid || 0);
        return {
          item_name: item.item_name || item.name || item.title || item.category_name || item.service_category?.name || 'N/A',
          category: item.category || item.category_name || item.service_category?.name || 'N/A',
          estimated_cost: est.toLocaleString(),
          actual_cost: act.toLocaleString(),
          paid_amount: paid.toLocaleString(),
          balance: (act > 0 ? act - paid : Math.max(est - paid, 0)).toLocaleString()
        };
      });
    } catch (err) {
      console.error('Failed to fetch budget export data:', err);
      return budgetItems.map(item => ({
        item_name: item.category_name,
        category: item.category_name,
        estimated_cost: (parseFloat(item.amount) || 0).toLocaleString(),
        actual_cost: (parseFloat(item.amount) || 0).toLocaleString(),
        paid_amount: (parseFloat(item.amount_paid) || 0).toLocaleString(),
        balance: item.remaining.toLocaleString()
      }));
    }
  };

  const fetchExpendituresData = async () => {
    try {
      const response = await apiClient.get('/export/expenditures/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.expenditures || dataPayload?.expenses || dataPayload?.data || dataPayload || [];
      const list = Array.isArray(rawData) && rawData.length > 0 ? rawData : budgetItems;
      return list.map(item => ({
        item_name: item.item_name || item.name || item.description || item.category_name || 'N/A',
        category: item.category || item.category_name || 'N/A',
        amount: parseFloat(item.amount || item.cost || item.actual_cost || 0).toLocaleString(),
        paid_to: item.paid_to || item.vendor || item.provider || 'N/A',
        date: item.date || item.created_at ? new Date(item.date || item.created_at).toLocaleDateString() : new Date().toLocaleDateString()
      }));
    } catch (err) {
      console.error('Failed to fetch expenditures export data:', err);
      return budgetItems.map(item => ({
        item_name: item.category_name,
        category: item.category_name,
        amount: (parseFloat(item.amount_paid) || 0).toLocaleString(),
        paid_to: 'N/A',
        date: new Date().toLocaleDateString()
      }));
    }
  };

  const handleExcelExport = async (type) => {
    toast.info(`Inapakia taarifa za ${type.toUpperCase()} Excel...`);
    try {
      const data = type === 'budget' ? await fetchBudgetData() : await fetchExpendituresData();
      if (!data || data.length === 0) {
        toast.warning('Hakuna data ya kuexport');
        return;
      }

      const columns = type === 'budget' ? budgetCols : expCols;
      const exportData = data.map(row => {
        const rowObj = {};
        columns.forEach(col => {
          rowObj[col.header] = row[col.dataKey] !== undefined && row[col.dataKey] !== null ? row[col.dataKey].toString() : '';
        });
        return rowObj;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, type === 'budget' ? 'Bajeti' : 'Matumizi');
      XLSX.writeFile(wb, `${type}_report_${currentEvent?.title || 'event'}.xlsx`);
      toast.success('Excel imepakuliwa kikamilifu!');
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Imeshindwa kupakua Excel');
    }
  };

  const handlePDFExport = async (type) => {
    toast.info(`Inapakia taarifa za ${type.toUpperCase()} PDF...`);
    try {
      const data = type === 'budget' ? await fetchBudgetData() : await fetchExpendituresData();
      if (!data || data.length === 0) {
        toast.warning('Hakuna data ya kuexport');
        return;
      }

      const columns = type === 'budget' ? budgetCols : expCols;
      const doc = new jsPDF();

      const titleToUse = `RIPOTI YA ${type === 'budget' ? 'BAJETI' : 'MATUMIZI'} - ${currentEvent?.title || currentEvent?.event_title || 'HARUSIONLINE'}`;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFont('courier');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(titleToUse, pageWidth / 2, 12, { align: 'center' });

      const tableColumn = columns.map(col => col.header);
      const tableRows = data.map(row => columns.map(col => row[col.dataKey] !== undefined && row[col.dataKey] !== null ? row[col.dataKey].toString() : ''));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 18,
        theme: 'grid',
        styles: {
          font: 'courier',
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 1.5,
          halign: 'center',
          valign: 'middle',
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        }
      });

      doc.save(`${type}_report_${currentEvent?.title || 'event'}.pdf`);
      toast.success('PDF imepakuliwa kikamilifu!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Imeshindwa kupakua PDF');
    }
  };

  const filteredBudgetItems = budgetItems.filter(item =>
    item.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalBudget = budgetItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const totalPaid = budgetItems.reduce((sum, i) => sum + (parseFloat(i.amount_paid) || 0), 0);
  const totalRemaining = totalBudget - totalPaid;
  const paidPct = totalBudget > 0 ? Math.min((totalPaid / totalBudget) * 100, 100) : 0;
  const remainingPct = totalBudget > 0 ? Math.min((totalRemaining / totalBudget) * 100, 100) : 0;

  const columns = [
    {
      name: 'Budget Category',
      selector: row => row.category_name,
      sortable: true,
      cell: row => (
        <div className="d-flex flex-column py-2">
          <span className="fw-bold" style={{ color: '#2b3445', fontSize: '0.95rem' }}>{row.category_name}</span>
        </div>
      ),
      minWidth: '220px'
    },
    {
      name: 'Estimated Budget',
      selector: row => parseFloat(row.amount) || 0,
      sortable: true,
      cell: row => (
        <span style={{ color: '#475569' }}>
          {row.amount_formatted}
        </span>
      ),
      width: '160px'
    },
    {
      name: 'Amount Paid',
      selector: row => parseFloat(row.amount_paid) || 0,
      sortable: true,
      cell: row => (
        <span style={{ color: '#10b981' }}>
          {row.amount_paid_formatted}
        </span>
      ),
      width: '160px'
    },
    {
      name: 'Utilization',
      cell: row => {
        const amt = parseFloat(row.amount) || 0;
        const paid = parseFloat(row.amount_paid) || 0;
        const itemPct = amt > 0 ? (paid / amt) * 100 : 0;
        const isOver = paid > amt;

        // Define colors based on progress
        let progressColor = 'linear-gradient(90deg, #34d399 0%, #10b981 100%)'; // Green
        if (isOver) progressColor = 'linear-gradient(90deg, #fbbf24 0%, #d97706 100%)'; // Orange
        else if (itemPct > 75) progressColor = 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)'; // Light Orange

        return (
          <div className="w-100 px-2 py-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Status</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: isOver ? '#d97706' : '#10b981' }}>
                {itemPct.toFixed(0)}%
              </span>
            </div>
            <div style={{ height: '8px', width: '100%', backgroundColor: '#f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(itemPct, 100)}%`,
                  background: progressColor,
                  borderRadius: '10px',
                  transition: 'width 0.5s ease-in-out'
                }}
              />
            </div>
          </div>
        );
      },
      width: '180px'
    },
    {
      name: 'Remaining Balance',
      selector: row => row.remaining,
      sortable: true,
      cell: row => {
        const isDeficit = (parseFloat(row.amount_paid) || 0) > (parseFloat(row.amount) || 0);
        return (
          <span
            className="px-2 py-1 rounded fw-semibold"
            style={{
              fontSize: '0.85rem',
              backgroundColor: isDeficit ? '#fef3c7' : '#f0fdf4',
              color: isDeficit ? '#d97706' : '#16a34a'
            }}
          >
            {row.remaining_formatted}
          </span>
        );
      },
      width: '180px'
    },
    {
      name: 'Notes',
      selector: row => row.description || '-',
      sortable: true,
      cell: row => <span className="text-muted" style={{ fontSize: '0.85rem', fontStyle: row.description ? 'normal' : 'italic' }}>{row.description || 'No description'}</span>,
      wrap: true
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-3 align-items-center">
          <FaEdit
            style={{ color: '#3b82f6', cursor: 'pointer', transition: 'transform 0.2s', padding: '2px' }}
            size={20}
            title="Edit Budget"
            onClick={() => handleEdit(row)}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
          />
          <FaTrash
            style={{ color: '#ef4444', cursor: 'pointer', transition: 'transform 0.2s', padding: '2px' }}
            size={20}
            title="Delete Budget"
            onClick={() => handleDeleteClick(row)}
            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        </div>
      ),
      width: '100px'
    }
  ];

  const steps = [ /* same as before */];

  // Loading spinner
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3" style={{ color: '#6b7280' }}>Loading budget data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Joyride steps={steps} run={runTour} continuous showSkipButton showProgress styles={{ options: { primaryColor: '#007bff', textColor: '#4a85ff' } }} />
      <div className="dashboard container mt-4">
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Bajeti</h1>

        {/* Progress Bars Overview */}
        <div className="bajeti-progress-section mt-4">
          <div className="bajeti-progress-card">
            <div className="bajeti-progress-header">
              <div className="d-flex align-items-center gap-2">
                <FaMoneyBillWave className="text-primary" />
                <span className="bajeti-progress-title">Budget Utilization</span>
              </div>
            </div>
            <div className="mt-3">
              <div className="bajeti-progress-header mb-1">
                <span className="bajeti-progress-title">Paid</span>
                <span className="bajeti-progress-pct" style={{ color: '#16a34a' }}>TZS {totalPaid.toLocaleString()}</span>
              </div>
              <div className="bajeti-progress-bar">
                <div className="bajeti-progress-fill bajeti-fill-green" style={{ width: `${paidPct}%` }}></div>
              </div>

              <div className="bajeti-progress-header mb-1 mt-3">
                <span className="bajeti-progress-title">Remaining</span>
                <span className="bajeti-progress-pct" style={{ color: '#ea580c' }}>TZS {totalRemaining.toLocaleString()}</span>
              </div>
              <div className="bajeti-progress-bar">
                <div className="bajeti-progress-fill bajeti-fill-orange" style={{ width: `${remainingPct}%` }}></div>
              </div>
            </div>
          </div>

          <div className="bajeti-progress-card">
            <div className="bajeti-progress-header">
              <div className="d-flex align-items-center gap-2">
                <FaCoins className="text-warning" />
                <span className="bajeti-progress-title">Budget vs Paid</span>
              </div>
            </div>
            <div className="mt-3">
              <div className="bajeti-progress-header mb-2 pb-2 border-bottom">
                <span className="bajeti-progress-title">Total Budget</span>
                <span className="bajeti-progress-pct" style={{ color: '#4338ca' }}>TZS {totalBudget.toLocaleString()}</span>
              </div>
              <div className="bajeti-progress-header">
                <span className="bajeti-progress-title">Total Paid</span>
                <span className="bajeti-progress-pct" style={{ color: '#16a34a' }}>TZS {totalPaid.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="dashboard row mt-4">
          <div className="dashboard col text-center">
            <div onClick={() => handlePDFExport('budget')} className="dashboard bg-dash p-4 rounded-lg budget-pdf" style={{ cursor: 'pointer' }}>
              <div className="dashText"><FaFilePdf className="mb-2" size={24} /><div>Budget PDF</div></div>
            </div>
          </div>

          <div className="dashboard col text-center">
            <div onClick={() => handleExcelExport('budget')} className="dashboard bg-dash p-4 rounded-lg budget-excel" style={{ cursor: 'pointer' }}>
              <div className="dashText"><FaFileExcel className="mb-2" size={24} /><div>Budget Excel</div></div>
            </div>
          </div>

          <div className="dashboard col text-center">
            <div onClick={() => handlePDFExport('expense')} className="dashboard bg-dash p-4 rounded-lg expense-pdf" style={{ cursor: 'pointer' }}>
              <div className="dashText"><FaFilePdf className="mb-2" size={24} /><div>Expense PDF</div></div>
            </div>
          </div>

          <div className="dashboard col text-center">
            <div onClick={() => handleExcelExport('expense')} className="dashboard bg-dash p-4 rounded-lg expense-excel" style={{ cursor: 'pointer' }}>
              <div className="dashText"><FaFileExcel className="mb-2" size={24} /><div>Expense Excel</div></div>
            </div>
          </div>

          <div className="dashboard col text-center">
            <div onClick={handleAddEdit} className="dashboard bg-dash p-4 rounded-lg new-budget" style={{ cursor: 'pointer' }}>
              <div className="dashText"><FaFileInvoice className="mb-2" size={24} /><div>New Budget</div></div>
            </div>
          </div>
        </div>

        {/* Budget Table */}
        <div className="mt-4 mb-5">
          <div className="card shadow-sm border-0 rounded-lg">
            <div className="card-header bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <h5 className="mb-0 fw-bold" style={{ color: '#24366b' }}>Budget Items</h5>
              <div className="d-flex gap-3 align-items-center">
                <Badge bg="primary" pill className="px-3 py-2 fs-6">{filteredBudgetItems.length} Items</Badge>
                <input
                  type="text"
                  placeholder="Search budget..."
                  className="form-control form-control-sm border-secondary"
                  style={{ width: '250px', borderRadius: '20px', padding: '0.4rem 1rem' }}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div className="card-body p-0">
              {error && <Alert variant="danger" className="mx-3 mt-3">{error}</Alert>}
              <StyledDataTable
                columns={columns}
                data={filteredBudgetItems}
                emptyMessage='No budget items found matching your search. Click "New Budget" to add.'
                paginationComponentOptions={{ noRowsPerPage: true }}
              />
            </div>
          </div>
        </div>

        {/* ── Separate Modals ── */}
        <OngezaBajetiModal
          show={showModal && !editingItem}
          onHide={() => setShowModal(false)}
          onSuccess={fetchBudgetItems}
        />

        <HaririBajetiModal
          show={showModal && !!editingItem}
          onHide={() => { setShowModal(false); setEditingItem(null); }}
          item={editingItem}
          onSuccess={fetchBudgetItems}
        />

        <FutaBajetiModal
          show={showDeleteModal}
          onHide={() => { setShowDeleteModal(false); setItemToDelete(null); }}
          item={itemToDelete}
          onSuccess={fetchBudgetItems}
        />

        <ProvidersBajetiModal
          show={showProvidersModal}
          onHide={() => { setShowProvidersModal(false); setSelectedCategory(null); }}
          category={selectedCategory}
        />

      </div>
    </>
  );
};

export default Bajeti;